import { jsPDF } from 'jspdf';
import { ReceiptApiData } from '../../shared/types';

interface Cell {
    label: string;
    value: string;
    /** fração da largura da linha (a soma da linha deve dar 1) */
    span: number;
    highlight?: boolean;
    note?: string;
}

interface Logo {
    base64: string;
    width: number;
    height: number;
}

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const VIA_HEIGHT = PAGE_HEIGHT / 2;
const MARGIN = 12;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const ROW_HEIGHT = 12;
const LOGO_MAX_PIXELS = 480;

const COLOR_TEXT: [number, number, number] = [33, 37, 41];
const COLOR_MUTED: [number, number, number] = [108, 117, 125];
const COLOR_BORDER: [number, number, number] = [206, 212, 218];
const COLOR_BRAND: [number, number, number] = [31, 63, 96];
const COLOR_HIGHLIGHT_BG: [number, number, number] = [232, 240, 248];

export class PdfGenerator {
    private static async loadImageAsBase64(url: string): Promise<string> {
        return new Promise((resolve, reject) => {
            fetch(url)
                .then(response => response.blob())
                .then(blob => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        resolve(reader.result as string);
                    };
                    reader.onerror = () => reject(new Error('Error reading image'));
                    reader.readAsDataURL(blob);
                })
                .catch(() => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.drawImage(img, 0, 0);
                            resolve(canvas.toDataURL('image/png'));
                        } else {
                            reject(new Error('Could not create canvas context'));
                        }
                    };
                    img.onerror = () => reject(new Error('Error loading image'));
                    img.crossOrigin = 'anonymous';
                    img.src = url;
                });
        });
    }

    /** o jsPDF grava a imagem descomprimida; reduzir evita um PDF de vários MB */
    private static downscaleImage(img: HTMLImageElement, maxPixels: number): string | null {
        if (img.width <= maxPixels) return null;
        const canvas = document.createElement('canvas');
        canvas.width = maxPixels;
        canvas.height = Math.round(img.height * (maxPixels / img.width));
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/png');
    }

    private static async loadLogo(): Promise<Logo | null> {
        try {
            const base64 = await this.loadImageAsBase64('/images/logo.png');
            const img = new Image();
            img.src = base64;
            await new Promise<void>((resolve) => {
                if (img.complete) {
                    resolve();
                } else {
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                }
            });

            if (!img.width || !img.height) return null;

            const base64Reduced = this.downscaleImage(img, LOGO_MAX_PIXELS) ?? base64;
            const maxWidth = 32;
            const maxHeight = 12;
            const aspectRatio = img.width / img.height;
            let width = maxWidth;
            let height = maxWidth / aspectRatio;
            if (height > maxHeight) {
                height = maxHeight;
                width = maxHeight * aspectRatio;
            }
            return { base64: base64Reduced, width, height };
        } catch (error) {
            console.warn('Error loading logo, continuing without it:', error);
            return null;
        }
    }

    private static formatDocument(value: string | null | undefined): string {
        if (!value) return '-';
        const digits = String(value).replace(/\D/g, '').replace(/^0+/, '');
        if (!digits) return '-';
        if (digits.length <= 11) {
            return digits.padStart(11, '0').replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
        }
        if (digits.length <= 14) {
            return digits.padStart(14, '0').replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
        }
        return digits;
    }

    private static parseNumber(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') return null;
        const num = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
        return isNaN(num) ? null : num;
    }

    private static formatMoney(value: number | null, decimals = 2): string {
        if (value === null) return '-';
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
    }

    private static formatInteger(value: number | null): string {
        return value === null ? '-' : value.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
    }

    private static formatDateTime(data: ReceiptApiData): string {
        if (data.datahora_formatada) return data.datahora_formatada;
        if (!data.datahora) return '-';
        const date = new Date(data.datahora);
        return isNaN(date.getTime()) ? data.datahora : date.toLocaleString('pt-BR');
    }

    private static buildRows(data: ReceiptApiData): Cell[][] {
        const valor = this.parseNumber(data.valor);
        const valorOriginal = this.parseNumber(data.valor_original);
        const quantidade = this.parseNumber(data.mig_tra_quantidade);
        const km = this.parseNumber(data.mig_tra_km);
        const kmAnterior = this.parseNumber(data.mig_tra_km_anterior);
        const precoUnitario = valor !== null && quantidade ? valor / quantidade : null;
        const kmRodados = km !== null && kmAnterior !== null && km >= kmAnterior ? km - kmAnterior : null;
        const temDesconto = valorOriginal !== null && valor !== null && Math.abs(valorOriginal - valor) >= 0.01;

        return [
            [
                { label: 'Autorização', value: String(data.mig_tra_autorizacao ?? '-'), span: 1 / 3 },
                { label: 'Documento', value: String(data.mig_tra_id ?? '-'), span: 1 / 3 },
                { label: 'Data / Hora', value: this.formatDateTime(data), span: 1 / 3 },
            ],
            [
                { label: 'Placa', value: data.placa || '-', span: 1 / 3 },
                { label: 'Cartão', value: data.cartao_final ? `**** **** **** ${data.cartao_final}` : '-', span: 1 / 3 },
                { label: 'Produto', value: data.produto || '-', span: 1 / 3 },
            ],
            [
                { label: 'KM / Hr atual', value: this.formatInteger(km), span: 1 / 3 },
                { label: 'KM / Hr anterior', value: this.formatInteger(kmAnterior), span: 1 / 3 },
                { label: 'KM / Hr percorrido', value: this.formatInteger(kmRodados), span: 1 / 3 },
            ],
            [
                {
                    label: 'Quantidade',
                    value: quantidade === null ? '-' : `${quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} L`,
                    span: 1 / 3,
                },
                { label: 'Preço unitário', value: this.formatMoney(precoUnitario, 3), span: 1 / 3 },
                {
                    label: 'Valor total',
                    value: this.formatMoney(valor),
                    span: 1 / 3,
                    highlight: true,
                    note: temDesconto ? `Original: ${this.formatMoney(valorOriginal)}` : undefined,
                },
            ],
            [
                { label: 'Cliente', value: data.cliente_razao || '-', span: 0.5 },
                { label: 'CNPJ / CPF cliente', value: this.formatDocument(data.cnpj_cliente), span: 0.25 },
                { label: 'Inscrição estadual', value: data.cliente_inscricao || '-', span: 0.25 },
            ],
            [
                { label: 'Motorista', value: data.motorista || '-', span: 0.75 },
                { label: 'Matrícula', value: data.matricula ? String(data.matricula) : '-', span: 0.25 },
            ],
        ];
    }

    private static fitText(doc: jsPDF, text: string, maxWidth: number): string {
        if (doc.getTextWidth(text) <= maxWidth) return text;
        let truncated = text;
        while (truncated.length > 1 && doc.getTextWidth(`${truncated}…`) > maxWidth) {
            truncated = truncated.slice(0, -1);
        }
        return `${truncated.trimEnd()}…`;
    }

    private static renderRow(doc: jsPDF, cells: Cell[], y: number): void {
        let x = MARGIN;
        const padding = 2.5;

        cells.forEach(cell => {
            const width = CONTENT_WIDTH * cell.span;

            if (cell.highlight) {
                doc.setFillColor(...COLOR_HIGHLIGHT_BG);
                doc.rect(x, y, width, ROW_HEIGHT, 'F');
            }
            doc.setDrawColor(...COLOR_BORDER);
            doc.setLineWidth(0.2);
            doc.rect(x, y, width, ROW_HEIGHT);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.5);
            doc.setTextColor(...COLOR_MUTED);
            doc.text(cell.label.toUpperCase(), x + padding, y + 4);

            if (cell.note) {
                doc.text(cell.note, x + width - padding, y + 4, { align: 'right' });
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(cell.highlight ? 11 : 9);
            doc.setTextColor(...(cell.highlight ? COLOR_BRAND : COLOR_TEXT));
            doc.text(this.fitText(doc, cell.value, width - padding * 2), x + padding, y + 9.5);

            x += width;
        });
    }

    private static renderVia(doc: jsPDF, data: ReceiptApiData, logo: Logo | null, viaLabel: string, offsetY: number): void {
        let y = offsetY + MARGIN;

        // cabeçalho: logo à esquerda, título e via à direita
        if (logo) {
            doc.addImage(logo.base64, 'PNG', MARGIN, y, logo.width, logo.height, 'logo', 'FAST');
        }
        doc.setTextColor(...COLOR_BRAND);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('COMPROVANTE DE ABASTECIMENTO', PAGE_WIDTH - MARGIN, y + 5, { align: 'right' });
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MUTED);
        doc.text(viaLabel, PAGE_WIDTH - MARGIN, y + 10.5, { align: 'right' });

        y += 15;
        doc.setDrawColor(...COLOR_BRAND);
        doc.setLineWidth(0.5);
        doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);

        // estabelecimento
        y += 6;
        doc.setTextColor(...COLOR_TEXT);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10.5);
        doc.text(this.fitText(doc, data.estabelecimento || '-', CONTENT_WIDTH), MARGIN, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...COLOR_MUTED);
        const estabelecimentoInfo = [`CNPJ: ${this.formatDocument(data.cnpj_estabelecimento)}`];
        if (data.inscricao_estabelecimento) {
            estabelecimentoInfo.push(`Inscrição estadual: ${data.inscricao_estabelecimento}`);
        }
        doc.text(estabelecimentoInfo.join('     '), MARGIN, y);

        // grade com os dados da venda
        y += 4;
        this.buildRows(data).forEach(row => {
            this.renderRow(doc, row, y);
            y += ROW_HEIGHT;
        });

        // assinatura
        const signatureY = offsetY + VIA_HEIGHT - 16;
        const signatureWidth = 80;
        const signatureX = (PAGE_WIDTH - signatureWidth) / 2;
        doc.setDrawColor(...COLOR_TEXT);
        doc.setLineWidth(0.2);
        doc.line(signatureX, signatureY, signatureX + signatureWidth, signatureY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...COLOR_MUTED);
        doc.text('Assinatura do motorista', PAGE_WIDTH / 2, signatureY + 3.5, { align: 'center' });

        doc.setFontSize(6.5);
        doc.text(`eFleet  •  Emitido em ${new Date().toLocaleString('pt-BR')}`, PAGE_WIDTH / 2, signatureY + 8.5, { align: 'center' });
    }

    private static renderCutLine(doc: jsPDF): void {
        const y = VIA_HEIGHT;
        doc.setDrawColor(...COLOR_MUTED);
        doc.setLineWidth(0.2);
        doc.setLineDashPattern([2, 1.5], 0);
        doc.line(4, y, PAGE_WIDTH - 4, y);
        doc.setLineDashPattern([], 0);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(...COLOR_MUTED);
        const label = 'corte aqui';
        const labelWidth = doc.getTextWidth(label) + 4;
        doc.setFillColor(255, 255, 255);
        doc.rect((PAGE_WIDTH - labelWidth) / 2, y - 1.5, labelWidth, 3, 'F');
        doc.text(label, PAGE_WIDTH / 2, y + 0.8, { align: 'center' });
    }

    /**
     * Converte o comprovante em texto (separado por @) retornado por versões antigas
     * do backend. Usado só como fallback quando o comprovante completo não pode ser buscado.
     */
    static fromReceiptText(receiptText: string, driverName?: string, driverRegistration?: string): ReceiptApiData {
        const lines = receiptText.split('@').map(line => line.trim());
        const find = (regex: RegExp): string | null => {
            for (const line of lines) {
                const match = line.match(regex);
                if (match) return match[1].trim();
            }
            return null;
        };
        const cartao = lines.find(line => /^\*{4,}\d{4}$/.test(line) || /^\*{8,}\d+/.test(line));

        return {
            mig_tra_id: Number(find(/DOC\s*=\s*(\d+)/i)) || 0,
            mig_tra_autorizacao: Number(find(/AUT\s*=\s*(\d+)/i)) || 0,
            datahora: find(/(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2})/) || '',
            datahora_formatada: find(/(\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}:\d{2})/),
            placa: find(/PLACA:\s*([A-Z0-9]+)/i),
            valor: (find(/VALOR:\s*R\$\s*([\d,.]+)/i) || '').replace(/\./g, '').replace(',', '.'),
            valor_original: null,
            mig_tra_quantidade: (find(/Qtde:\s*([\d,.]+)/i) || '').replace(',', '.'),
            mig_tra_km: Number(find(/KM\s*\/\s*H[r]?:\s*(\d+)/i)) || null,
            mig_tra_km_anterior: null,
            cartao_final: cartao ? cartao.slice(-4) : null,
            produto: find(/Produto:\s*(.+)/i),
            cliente_razao: null,
            cnpj_cliente: find(/CNPJ Cliente:\s*(\d+)/i),
            cliente_inscricao: null,
            estabelecimento: find(/POSTO:?\s+(.+)/i),
            cnpj_estabelecimento: find(/^CNPJ:\s*([\d\s]+)/i),
            inscricao_estabelecimento: find(/INSCRICAO ESTADUAL:\s*(\d+)/i),
            motorista: driverName && driverName !== 'Motorista não identificado' ? driverName : null,
            matricula: driverRegistration || null,
        };
    }

    /** Gera o comprovante em uma folha A4 com a via do estabelecimento e a do cliente. */
    static async generateReceipt(data: ReceiptApiData): Promise<void> {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const logo = await this.loadLogo();

        this.renderVia(doc, data, logo, '1ª VIA - ESTABELECIMENTO', 0);
        this.renderCutLine(doc);
        this.renderVia(doc, data, logo, '2ª VIA - CLIENTE', VIA_HEIGHT);

        const date = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const filename = `comprovante_${data.mig_tra_autorizacao || ''}_${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}.pdf`;

        doc.save(filename);
    }
}
